// frontend/src/pages/marketplace/MarketplacePage.tsx
// Main marketplace homepage with featured items and category browsing

import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  TrendingUp,
  Shield,
  Star,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import {
  MarketplaceSearch,
  CategoryNav,
  ProductCard,
  VendorCard,
} from '../components';
import { useFeatured, useProducts, useVendors } from '../hooks/useMarketplace';

const MarketplacePage: React.FC = () => {
  const { data: featured, isLoading: featuredLoading } = useFeatured();
  const { data: newProducts, isLoading: newProductsLoading } = useProducts({
    sortBy: 'created_at',
    sortOrder: 'desc',
    limit: 8,
  });
  const { data: topVendors, isLoading: vendorsLoading } = useVendors({
    isVerified: true,
    sortBy: 'rating',
    sortOrder: 'desc',
    limit: 4,
  });

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-navy to-navy-light overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-display text-3xl md:text-5xl font-medium text-white mb-4">
              Trusted Equipment & Services
            </h1>
            <p className="text-lg text-white/80 mb-8">
              Vetted vendors, real family reviews, and competitive pricing on everything
              your family needs.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <MarketplaceSearch
                variant="hero"
                placeholder="Search Accessibilitys, therapy equipment, home mods..."
              />
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              <div className="flex items-center gap-2 text-white/90">
                <Shield className="w-5 h-5 text-sage-light" />
                <span className="text-sm">Verified Vendors</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <Star className="w-5 h-5 text-terracotta-light" />
                <span className="text-sm">Real Family Reviews</span>
              </div>
              <div className="flex items-center gap-2 text-white/90">
                <ShoppingBag className="w-5 h-5 text-sage-light" />
                <span className="text-sm">Insurance Assistance</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <CategoryNav variant="horizontal" showCounts={false} />
        </div>
      </section>

      {/* Featured Products */}
      {featured?.featuredProducts && featured.featuredProducts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-terracotta/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-terracotta" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-navy">
                  Featured Products
                </h2>
                <p className="text-sm text-warm-gray">
                  Hand-picked selections from top vendors
                </p>
              </div>
            </div>
            <Link
              to="/marketplace/products"
              className="hidden md:flex items-center gap-1 text-sage hover:text-sage-dark font-medium transition-colors"
            >
              View all
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {featuredLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-sage animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.featuredProducts.slice(0, 4).map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  showWishlist
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Categories Grid */}
      <section className="bg-cream-dark py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl font-semibold text-navy mb-2">
              Browse by Category
            </h2>
            <p className="text-warm-gray">
              Find exactly what you need across our specialized categories
            </p>
          </div>

          <CategoryNav variant="dropdown" showCounts />
        </div>
      </section>

      {/* New Arrivals */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-display text-xl font-semibold text-navy">
              New Arrivals
            </h2>
            <p className="text-sm text-warm-gray">
              Recently added products from our vendors
            </p>
          </div>
          <Link
            to="/marketplace/products?sort=newest"
            className="hidden md:flex items-center gap-1 text-sage hover:text-sage-dark font-medium transition-colors"
          >
            See all new
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {newProductsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-sage animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newProducts?.items?.slice(0, 8).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                variant="compact"
                showWishlist
              />
            ))}
          </div>
        )}
      </section>

      {/* Top Vendors */}
      <section className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-sage" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-navy">
                  Verified Vendors
                </h2>
                <p className="text-sm text-warm-gray">
                  Trusted providers reviewed by families like yours
                </p>
              </div>
            </div>
            <Link
              to="/marketplace/vendors"
              className="hidden md:flex items-center gap-1 text-sage hover:text-sage-dark font-medium transition-colors"
            >
              All vendors
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {vendorsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-sage animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {topVendors?.items?.slice(0, 4).map((vendor) => (
                <VendorCard key={vendor.id} vendor={vendor} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-sage to-sage-dark py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-white mb-4">
            Are You a Vendor?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Join our marketplace to connect with families who need your products and services.
            Get verified, build trust, and grow your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/vendor/register"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-sage font-semibold rounded-lg hover:bg-cream transition-colors"
            >
              Become a Vendor
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/vendor/learn-more"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MarketplacePage;


